/**
 * Long text on one line — brief part 1 §1 (pkgC2), client rule 2, `docs/adr/9028`.
 *
 * A table row is one line. A text longer than its column is cut with an ellipsis
 * (`white-space: nowrap; text-overflow: ellipsis`), and the whole text is in the tooltip,
 * which opens on keyboard focus as well as on hover (G2 §2.6: no mouse-only affordance).
 * Only a text that IS cut becomes a tab stop: a column of short names adds nothing to the
 * keyboard path. A screen reader reads the whole text either way — the ellipsis is paint, the
 * text node is complete — so nothing is hidden behind a width (ADR 0014's objection to a bare
 * ellipsis, answered).
 *
 * `detail` is for a line whose tooltip says MORE than its text — a decision's reason with the
 * day it was decided. With `detail` the tooltip is always offered, cut or not, and `detail`
 * must itself carry the text, because it replaces it in the tooltip.
 *
 * Whether the text is cut is measured, not guessed: `scrollWidth > clientWidth`, again on every
 * resize, through ONE `ResizeObserver` shared by every truncated text on the page (a fifty-row
 * table with three text columns would otherwise hold 150 observers).
 */

import { useLayoutEffect, useState } from 'react';

import { FOCUS_RING_INSET } from './focus';
import { Tooltip } from './overlays';

type Measure = () => void;

let shared: ResizeObserver | null = null;
const measures = new WeakMap<Element, Measure>();

/** Observe `element`'s size with the page's one observer; returns the unobserve. */
function observe(element: Element, measure: Measure): () => void {
  if (typeof ResizeObserver === 'undefined') return () => undefined;
  shared ??= new ResizeObserver((entries) => {
    for (const entry of entries) measures.get(entry.target)?.();
  });
  measures.set(element, measure);
  shared.observe(element);
  return () => {
    shared?.unobserve(element);
    measures.delete(element);
  };
}

export interface TruncatedTextProps {
  /** The whole text. Drawn on one line, cut with an ellipsis where the column is narrower. */
  readonly text: string;
  /**
   * A tooltip that says more than the text (it must contain the text). Offered whether or not
   * the text is cut.
   */
  readonly detail?: string;
  /**
   * The line's own classes — its colour, its weight. The widest a truncated text grows is
   * 20rem (`max-w-[20rem]`), so it cuts inside an auto-width table column; a class here can
   * set another bound.
   */
  readonly className?: string;
}

export function TruncatedText({ text, detail, className = '' }: TruncatedTextProps): React.JSX.Element {
  // A callback ref as state, so a remount (the span moving in or out of the tooltip) is
  // measured and observed again rather than left observing the detached node.
  const [element, setElement] = useState<HTMLSpanElement | null>(null);
  const [cut, setCut] = useState(false);
  useLayoutEffect(() => {
    if (element === null) return undefined;
    const measure = (): void => setCut(element.scrollWidth > element.clientWidth);
    measure();
    // Once the web fonts are in, again: a text that fitted in the fallback face can overflow in
    // the real one while its box keeps its size, which the observer never reports (DEC-111
    // residual D — ids cut with no tooltip on production).
    let live = true;
    const fonts = typeof document === 'undefined' ? undefined : (document as Partial<Pick<Document, 'fonts'>>).fonts;
    void fonts?.ready.then(() => { if (live) measure(); });
    const unobserve = observe(element, measure);
    return () => { live = false; unobserve(); };
  }, [element, text]);

  const tip = detail ?? (cut ? text : undefined);
  const line = (
    <span
      ref={setElement}
      className={`block max-w-[20rem] truncate ${tip === undefined ? '' : FOCUS_RING_INSET} ${className}`}
      data-truncated={cut ? '' : undefined}
      tabIndex={tip === undefined ? undefined : 0}
    >
      {text}
    </span>
  );
  return tip === undefined ? line : <Tooltip content={tip}>{line}</Tooltip>;
}
